/**
 * Canonical retreat URL slug. Prefer the DB `slug` column (deduplicated at ingest);
 * fall back to computing from location + name when slug is missing.
 */
export function computeRetreatSlug(retreat) {
  const loc = (retreat.location_tags || retreat.location || "india")
    .split(",")[0]
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const name = (retreat.retreat_name || "yoga-retreat")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  return `${loc}-${name}`;
}

export function retreatSlug(retreat) {
  if (retreat?.slug) return retreat.slug;
  return computeRetreatSlug(retreat);
}

function pickPositiveNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

/** Parse batches whether the API returns an array or JSON string. */
export function normalizeRetreatBatches(retreat) {
  if (!retreat?.batches) return [];
  if (Array.isArray(retreat.batches)) return retreat.batches;
  if (typeof retreat.batches !== "string" || !retreat.batches.trim()) return [];
  try {
    const parsed = JSON.parse(retreat.batches);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatOfferPrice(num) {
  return String(Math.round(num));
}

function batchInrPrices(batches) {
  return batches
    .flatMap((batch) => [
      pickPositiveNumber(batch.price_shared_discount_inr),
      pickPositiveNumber(batch.price_private_discount_inr),
      // Soft migration: source fields if explicitly INR-like amounts missing
      pickPositiveNumber(batch.price_2_sharing_discount),
      pickPositiveNumber(batch.price_private_discount),
    ])
    .filter((value) => value != null);
}

/**
 * Resolve a schema.org Offer price in INR (stable base for SEO).
 */
export function resolveRetreatOffer(retreat) {
  if (!retreat) return null;

  const baseInr = resolveBasePriceInr(retreat);
  if (baseInr != null) {
    return { price: formatOfferPrice(baseInr), priceCurrency: "INR" };
  }

  return null;
}

/**
 * Resolve the single source-of-truth INR base price for a retreat/offering.
 * Prefer price_inr, then INR-sourced clean amounts, then batch INR mins.
 * Soft-migration: if only legacy USD columns exist, caller may convert with rates;
 * this helper never mixes foreign amounts as if they were INR unless source says so.
 */
export function resolveBasePriceInr(retreat, rates = null) {
  if (!retreat) return null;

  const directInr = pickPositiveNumber(retreat.price_inr);
  if (directInr) return directInr;

  const sourceCur = String(retreat.price_source_currency || retreat.currency || "").toUpperCase();
  const sourceAmount = pickPositiveNumber(
    retreat.price_source_amount ?? retreat.price_clean ?? retreat.price
  );

  if (sourceAmount != null) {
    if (sourceCur === "INR" || sourceCur === "RS" || sourceCur === "₹") {
      return sourceAmount;
    }
    // Soft convert foreign source → INR when rates available
    if (rates && sourceCur && rates[sourceCur]) {
      const rate = Number(rates[sourceCur]);
      if (Number.isFinite(rate) && rate > 0) return sourceAmount / rate;
    }
  }

  // Soft migration: reverse-convert from legacy foreign columns using rates
  if (rates) {
    for (const cur of ["USD", "GBP", "EUR"]) {
      const foreign = pickPositiveNumber(retreat[`price_${cur.toLowerCase()}`]);
      const rate = Number(rates[cur]);
      if (foreign && Number.isFinite(rate) && rate > 0) {
        return foreign / rate;
      }
    }
  }

  // Legacy path without rates: if price_clean looks INR-like and source missing, prefer price_usd reverse not done
  if (sourceCur === "INR" || !sourceCur) {
    const clean = pickPositiveNumber(retreat.price_clean);
    if (clean && (sourceCur === "INR" || pickPositiveNumber(retreat.price_inr))) return clean;
  }

  const batches = normalizeRetreatBatches(retreat);
  if (batches.length > 0) {
    const prices = batchInrPrices(batches);
    if (prices.length > 0) return Math.min(...prices);
  }

  // Last resort existing stock: many rows already have price_inr from Spark
  return pickPositiveNumber(retreat.price_clean);
}

/**
 * @deprecated Prefer resolveBasePriceInr + convertFromInr.
 * Kept for callers that still pass a display currency: returns INR base only
 * when currency is INR; otherwise returns converted amount if rates provided.
 */
export function resolveRetreatPrice(retreat, currency = "INR", rates = null) {
  const baseInr = resolveBasePriceInr(retreat, rates);
  if (baseInr == null) return null;
  const cur = String(currency || "INR").toUpperCase();
  if (cur === "INR") return baseInr;
  if (!rates) {
    // Fallback to precomputed column if present (legacy)
    const legacy = pickPositiveNumber(retreat[`price_${cur.toLowerCase()}`]);
    if (legacy) return legacy;
    return baseInr;
  }
  const rate = Number(rates[cur]);
  if (!Number.isFinite(rate) || rate <= 0) return baseInr;
  return baseInr * rate;
}
